import { CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import {
  useGenerateCampaignMutation,
  useGetGenerateCampaignResultQuery,
} from "../../../../api";
import { ProductAnalysisResponse } from "../../../../types/apiTypes";
import { IconWrapper } from "../../IconWrapper";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { AnalysisStatus, SSEEvents } from "../../../../types/companyTypes";
import { setStatus } from "features/companySlice";

type Props = {
  files: File[];
  productUrl: string;
  setResultHandler: (result: ProductAnalysisResponse) => void;
  stopAnalyzeHandler: () => void;
};

export const AnalysePDFBlock = ({
  files,
  setResultHandler,
  productUrl,
  stopAnalyzeHandler,
}: Props) => {
  const dispatch = useAppDispatch();
  const { generation_status } = useAppSelector((state) => state.company.status);

  const [progress, setProgress] = useState<number>(0);
  const [getResult, setGetResult] = useState<boolean>(false);
  const [generateCampaign, { data: generateData }] =
    useGenerateCampaignMutation();

  const { data: generateCampaignResult } = useGetGenerateCampaignResultQuery(
    generateData?.task_id || "",
    {
      skip: !getResult || !generateData?.task_id,
      refetchOnMountOrArgChange: true,
    },
  );

  const handleStatusUpdate = (
    status: typeof generation_status,
    source: string,
  ) => {
    if (!status) return;

    console.log(
      `[Campaign][${source}] Handling status | progress=${status.progress}% | status=${status.status}`,
    );

    setProgress(status.progress);

    if (status.progress === 100) {
      console.log(
        `[Campaign][${source}] Progress 100% → triggering result fetch`,
      );
      setGetResult(true);
    }

    if (status.status === AnalysisStatus.failed) {
      console.log(`[Campaign][${source}] Status failed → stopping analyze`);
      stopAnalyzeHandler();
      dispatch(setStatus({ [SSEEvents.generation_status]: undefined }));
    }
  };

  useEffect(() => {
    console.log(
      `[Campaign] Mounting, starting generation | url=${productUrl} | files=${files.length}`,
    );
    generateCampaign({ url: productUrl || undefined, pdf_files: files })
      .unwrap()
      .then((data) => console.log(`[Campaign] Task created: ${data.task_id}`))
      .catch((err) =>
        console.error(`[Campaign] Failed to start generation`, err),
      );
  }, []);

  useEffect(() => {
    if (!generateData?.task_id) return;

    console.log(
      `[Campaign][catch-up] task_id received: ${generateData.task_id} | generation_status in Redux: ${generation_status?.task_id ?? "none"}`,
    );

    if (!generation_status) {
      console.log(
        `[Campaign][catch-up] No SSE in Redux yet, normal flow will handle it`,
      );
      return;
    }

    if (generation_status.task_id !== generateData.task_id) {
      console.warn(
        `[Campaign][catch-up] task_id mismatch | Redux has=${generation_status.task_id} | expected=${generateData.task_id}`,
      );
      return;
    }

    console.log(
      `[Campaign][catch-up] SSE was already in Redux, processing it now`,
    );
    handleStatusUpdate(generation_status, "catch-up");
  }, [generateData?.task_id]);

  useEffect(() => {
    if (!generation_status) return;

    console.log(
      `[Campaign][sse] Event received | task=${generation_status.task_id} | ` +
        `progress=${generation_status.progress}% | status=${generation_status.status} | ` +
        `expected=${generateData?.task_id ?? "not yet"}`,
    );

    if (!generateData?.task_id) {
      console.warn(
        `[Campaign][sse] task_id not yet available, SSE will be caught up later`,
      );
      return;
    }

    if (generation_status.task_id !== generateData.task_id) {
      console.warn(
        `[Campaign][sse] task_id mismatch, ignoring | got=${generation_status.task_id} | expected=${generateData.task_id}`,
      );
      return;
    }

    handleStatusUpdate(generation_status, "sse");
  }, [generation_status]);

  useEffect(() => {
    if (!generateCampaignResult) return;

    console.log(`[Campaign] Result received → calling setResultHandler`);
    setResultHandler(generateCampaignResult);
    setGetResult(false);
  }, [generateCampaignResult]);

  return (
    <Stack
      sx={{
        padding: "36px",
        borderRadius: "8px",
        textAlign: "center",
        backgroundColor: "#FCFCFC",
        transition: "background-color 0.2s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      <IconWrapper width="34px" height="34px">
        <CircularProgress size={24} sx={{ color: "#4C545B" }} />
      </IconWrapper>
      <Typography
        variant="button"
        color="#4C545B"
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {`Analyzing: ${progress}%`}
      </Typography>
    </Stack>
  );
};
